'use client';

// DEBUG: Set to true to enable detailed history sync logs
const DEBUG_MAP_HISTORY_SYNC = false;

import { useCallback, useEffect, useRef } from 'react';
import {
  isValidMapView,
  readCurrentSearchMapHistoryView,
  refineCurrentSearchMapHistoryView,
} from '@/src/lib/map/searchMapHistoryState';
import { stripSrpPageSegment } from '@/src/lib/seo/srp-pagination';

const POSITION_EPSILON = 0.00005;
const ZOOM_EPSILON = 0.005;

type MapView = {
  lat: number;
  lng: number;
  zoom: number;
};

type UseMapHistorySyncOptions = {
  historyMode?: 'push' | 'none';
};

// Instance-based tracking for skipping URL updates during initial fitBounds
// Uses WeakMap to scope skip state to specific map instances, preventing issues with:
// 1. Multiple maps on the same page
// 2. React Strict Mode double-rendering
// 3. Map remounts (e.g., theme changes)
const skipNextIdleMap = new WeakMap<google.maps.Map, boolean>();

/**
 * Function to skip the next idle event's URL update for a specific map instance
 * Used by AreaBoundary to prevent URL params during initial fitBounds
 *
 * @param map - The Google Maps instance to skip the next idle update for
 */
export function skipNextIdleUpdate(map: google.maps.Map | null) {
  if (map) {
    skipNextIdleMap.set(map, true);
  }
}

// Separate flag for suppressing a single URL pushState from a programmatic pan
// (e.g., panning to a pinned marker). Unlike skipNextIdleUpdate, this only
// suppresses the URL update — it doesn't affect the initialization state machine.
const suppressNextUrlUpdateMap = new WeakMap<google.maps.Map, boolean>();

/**
 * Suppress the next URL pushState that would be triggered by a map idle event.
 * Used for programmatic pans (e.g., centering on a pinned marker) that should
 * not create browser history entries.
 */
export function suppressNextUrlUpdate(map: google.maps.Map | null) {
  if (map) {
    suppressNextUrlUpdateMap.set(map, true);
  }
}

// Marks a map that is restoring a position from browser history (popstate →
// programmatic panTo/setZoom) until that restore settles. isHandlingHistoryRef
// only guards this hook's own idle → pushState path; MapClient's separate
// bounds idle listener consults this marker so the restore is republished with
// source 'restore' instead of being mistaken for a live user pan (HP-8603).
const historyRestoreInFlightMap = new WeakMap<google.maps.Map, boolean>();

export function isHistoryRestoreInFlight(map: google.maps.Map | null): boolean {
  return map ? (historyRestoreInFlightMap.get(map) ?? false) : false;
}

/**
 * Fresh user intent supersedes an in-flight history restore: MapClient calls
 * this the moment a user-initiated gesture (pointer, wheel, touch, keyboard,
 * drag) begins, so that gesture's idle publishes as a live source 'map' write
 * instead of inheriting the restore tag (HP-8603).
 */
export function clearHistoryRestoreInFlight(map: google.maps.Map | null) {
  if (map) {
    historyRestoreInFlightMap.delete(map);
  }
}

function shouldSkipNextIdle(map: google.maps.Map): boolean {
  return skipNextIdleMap.get(map) ?? false;
}

function clearSkipNextIdle(map: google.maps.Map): void {
  skipNextIdleMap.delete(map);
}

function mapAlreadyMatchesPosition(map: google.maps.Map, lat: number, lng: number, zoom: number): boolean {
  const center = map.getCenter();
  const currentZoom = map.getZoom();

  if (!center || currentZoom === undefined) {
    return false;
  }

  return (
    Math.abs(center.lat() - lat) <= POSITION_EPSILON &&
    Math.abs(center.lng() - lng) <= POSITION_EPSILON &&
    Math.abs(currentZoom - zoom) <= ZOOM_EPSILON
  );
}

function mapViewChanged(previous: MapView | null, next: MapView): boolean {
  if (!previous) return true;
  return (
    Math.abs(previous.lat - next.lat) > POSITION_EPSILON ||
    Math.abs(previous.lng - next.lng) > POSITION_EPSILON ||
    Math.abs(previous.zoom - next.zoom) > ZOOM_EPSILON
  );
}

function urlAlreadyMatchesPosition(params: URLSearchParams, lat: number, lng: number, zoom: number): boolean {
  const urlLat = Number.parseFloat(params.get('lat') ?? '');
  const urlLng = Number.parseFloat(params.get('lon') ?? '');
  const urlZoom = Number.parseFloat(params.get('z') ?? '');

  return (
    Number.isFinite(urlLat) &&
    Number.isFinite(urlLng) &&
    Number.isFinite(urlZoom) &&
    Math.abs(urlLat - lat) <= POSITION_EPSILON &&
    Math.abs(urlLng - lng) <= POSITION_EPSILON &&
    Math.abs(urlZoom - zoom) <= ZOOM_EPSILON
  );
}

function readMapViewFromSearchParams(params: URLSearchParams): MapView | null {
  const lat = Number.parseFloat(params.get('lat') ?? '');
  const lng = Number.parseFloat(params.get('lon') ?? '');
  const zoom = Number.parseFloat(params.get('z') ?? '');

  return isValidMapView({ center: { lat, lng }, zoom }) ? { lat, lng, zoom } : null;
}

function readRestorableMapView(params: URLSearchParams, historyState: unknown): MapView | null {
  const urlView = readMapViewFromSearchParams(params);
  if (urlView) return urlView;
  if (params.has('lat') || params.has('lon') || params.has('z')) return null;

  const historyView = readCurrentSearchMapHistoryView(historyState);
  return historyView
    ? { lat: historyView.center.lat, lng: historyView.center.lng, zoom: historyView.zoom }
    : null;
}

/**
 * Manages map-URL sync with proper history support
 *
 * Behaviors:
 * 1. User pans/zooms → pushState (doesn't affect map)
 * 2. Browser back/forward → updates map
 * 3. No re-renders interfere with map
 * 4. Initial fitBounds → skips URL update (keeps URL clean)
 */
export function useMapHistorySync(
  map: google.maps.Map | null,
  { historyMode = 'push' }: UseMapHistorySyncOptions = {},
) {
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isHandlingHistoryRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const lastMapInstanceRef = useRef<google.maps.Map | null>(null);
  const lastObservedViewRef = useRef<MapView | null>(null);
  const hasUserInteractedWithMapRef = useRef(false);

  /**
   * Update URL via pushState (doesn't trigger navigation)
   */
  const updateURL = useCallback((lat: number, lng: number, zoom: number, scheduledHref: string) => {
    if (historyMode === 'none' || isHandlingHistoryRef.current) {
      return;
    }

    const currentHref = `${window.location.pathname}${window.location.search}`;
    if (currentHref !== scheduledHref) {
      return;
    }

    const roundedLat = Math.round(lat * 10000) / 10000;
    const roundedLng = Math.round(lng * 10000) / 10000;
    const roundedZoom = Math.round(zoom * 100) / 100;

    const params = new URLSearchParams(window.location.search);
    if (urlAlreadyMatchesPosition(params, roundedLat, roundedLng, roundedZoom)) {
      return;
    }

    params.set('lat', roundedLat.toFixed(4));
    params.set('lon', roundedLng.toFixed(4));
    params.set('z', roundedZoom.toFixed(2));
    params.delete('page');

    const newPathname = stripSrpPageSegment(window.location.pathname);
    const newUrl = `${newPathname}?${params.toString()}`;
    if (newUrl === currentHref) return;
    window.history.pushState(null, '', newUrl);
  }, [historyMode]);

  /**
   * Handle map instance changes (e.g., theme change causes remount)
   * Restores position from the current URL or history entry when map instance changes
   */
  useEffect(() => {
    if (!map || map === lastMapInstanceRef.current) {
      if (DEBUG_MAP_HISTORY_SYNC) {
        console.log('[useMapHistorySync] Map instance effect skipped:', {
          hasMap: !!map,
          isSameMap: map === lastMapInstanceRef.current,
        });
      }
      return;
    }

    const isMapRemount = lastMapInstanceRef.current !== null;
    hasInitializedRef.current = false;
    lastObservedViewRef.current = null;
    hasUserInteractedWithMapRef.current = false;
    lastMapInstanceRef.current = map;

    const params = new URLSearchParams(window.location.search);
    const restoreView = readRestorableMapView(params, isMapRemount ? window.history.state : null);

    if (DEBUG_MAP_HISTORY_SYNC) {
      const currentCenter = map.getCenter();
      console.log('[useMapHistorySync] NEW MAP INSTANCE DETECTED!', {
        urlParams: Object.fromEntries(params.entries()),
        currentMapPosition: currentCenter ? { lat: currentCenter.lat(), lng: currentCenter.lng() } : 'NO CENTER',
        willRestoreView: !!restoreView,
      });
    }

    if (restoreView) {
      const { lat: newLat, lng: newLng, zoom: newZoom } = restoreView;

      if (DEBUG_MAP_HISTORY_SYNC) {
        console.log('[useMapHistorySync] RESTORING POSITION FOR CURRENT ENTRY!', {
          newLat,
          newLng,
          newZoom,
        });
      }

      if (mapAlreadyMatchesPosition(map, newLat, newLng, newZoom)) {
        lastObservedViewRef.current = { lat: newLat, lng: newLng, zoom: newZoom };
        return;
      }

      isHandlingHistoryRef.current = true;
      let idleListener: google.maps.MapsEventListener | null = null;
      let resetTimer: ReturnType<typeof setTimeout> | null = null;
      let restoreTimer: ReturnType<typeof setTimeout> | null = null;

      const markProgrammaticRestoreComplete = () => {
        const restoredCenter = map.getCenter();
        const restoredZoom = map.getZoom();
        if (restoredCenter && restoredZoom !== undefined) {
          lastObservedViewRef.current = {
            lat: restoredCenter.lat(),
            lng: restoredCenter.lng(),
            zoom: restoredZoom,
          };
        }
        hasInitializedRef.current = true;
      };

      const resetHistoryHandling = () => {
        isHandlingHistoryRef.current = false;
        if (idleListener) {
          google.maps.event.removeListener(idleListener);
          idleListener = null;
        }
        if (resetTimer) {
          clearTimeout(resetTimer);
          resetTimer = null;
        }
      };

      idleListener = map.addListener('idle', () => {
        setTimeout(() => {
          markProgrammaticRestoreComplete();
          resetHistoryHandling();
        }, 0);
      });

      resetTimer = setTimeout(() => {
        markProgrammaticRestoreComplete();
        resetHistoryHandling();
      }, 1000);

      // Use setTimeout(0) to defer execution until after current call stack completes.
      // This ensures map instance is fully initialized and ready for position
      // changes, and the history guard above prevents the resulting idle from
      // rewriting the shared URL during initial restoration.
      restoreTimer = setTimeout(() => {
        if (map) {
          if (DEBUG_MAP_HISTORY_SYNC) {
            console.log('[useMapHistorySync] EXECUTING panTo/setZoom for current entry!');
          }
          map.panTo({ lat: newLat, lng: newLng });
          if (map.getZoom() !== newZoom) {
            map.setZoom(newZoom);
          }
        }
      }, 0);

      return () => {
        if (restoreTimer) {
          clearTimeout(restoreTimer);
        }
        resetHistoryHandling();
      };
    } else if (DEBUG_MAP_HISTORY_SYNC) {
      console.log('[useMapHistorySync] No URL params - NOT restoring position');
    }
  }, [map]);

  useEffect(() => {
    if (!map) return;

    hasUserInteractedWithMapRef.current = false;

    const markMapUserIntent = () => {
      hasUserInteractedWithMapRef.current = true;
    };

    const mapDiv = map.getDiv();
    const pointerOptions: AddEventListenerOptions = { capture: true };
    const passivePointerOptions: AddEventListenerOptions = { capture: true, passive: true };
    mapDiv.addEventListener('pointerdown', markMapUserIntent, pointerOptions);
    mapDiv.addEventListener('wheel', markMapUserIntent, passivePointerOptions);
    mapDiv.addEventListener('touchstart', markMapUserIntent, passivePointerOptions);
    mapDiv.addEventListener('keydown', markMapUserIntent, pointerOptions);
    const dragStartListener = map.addListener('dragstart', markMapUserIntent);

    return () => {
      mapDiv.removeEventListener('pointerdown', markMapUserIntent, true);
      mapDiv.removeEventListener('wheel', markMapUserIntent, true);
      mapDiv.removeEventListener('touchstart', markMapUserIntent, true);
      mapDiv.removeEventListener('keydown', markMapUserIntent, true);
      google.maps.event.removeListener(dragStartListener);
    };
  }, [map]);

  /**
   * Handle map idle event (fires when ALL panning/zooming finishes)
   *
   * This implements a state machine with multiple phases:
   * 1. Browser history navigation (skip URL updates)
   * 2. First idle - initialization (conditional URL sync)
   * 3. Second idle - after fitBounds (skip URL update)
   * 4. Subsequent idles - user interaction (debounced URL updates)
   */
  useEffect(() => {
    if (!map) {
      return;
    }

    // ═══════════════════════════════════════════════════════════
    // LATE ATTACHMENT DETECTION
    // ═══════════════════════════════════════════════════════════
    // If skip is already set for this map when we attach, it means:
    // 1. AreaBoundary has already called skipNextIdleUpdate(map)
    // 2. AreaBoundary has already called fitBounds()
    // 3. We're attaching to the map AFTER these operations
    //
    // The next idle event will be fitBounds completing, which should NOT
    // update the URL (to keep URLs clean on initial load). We mark as
    // initialized to skip the "FIRST IDLE" logic, but keep skip=true
    // so the next idle will be handled as "SECOND IDLE" (clears flag, no URL update).
    if (shouldSkipNextIdle(map) && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // Keep skip flag so next idle clears it without updating URL
    }

    const handleIdle = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();

      if (!center || zoom === undefined) {
        if (DEBUG_MAP_HISTORY_SYNC) {
          console.log('[useMapHistorySync] handleIdle: No center or zoom yet');
        }
        return;
      }

      const lat = center.lat();
      const lng = center.lng();
      const nextView = { lat, lng, zoom };

      if (DEBUG_MAP_HISTORY_SYNC) {
        console.log('[useMapHistorySync] handleIdle fired:', {
          position: { lat, lng, zoom },
          isHandlingHistory: isHandlingHistoryRef.current,
          hasInitialized: hasInitializedRef.current,
          shouldSkip: shouldSkipNextIdle(map),
        });
      }

      // ═══════════════════════════════════════════════════════════
      // GUARD 1: Skip during browser history navigation
      // ═══════════════════════════════════════════════════════════
      // When user clicks back/forward, we programmatically move the map via panTo/setZoom.
      // This triggers the 'idle' event, but we don't want to push that to history.
      // The flag is set in handlePopState and cleared after idle completes.
      if (isHandlingHistoryRef.current) {
        if (DEBUG_MAP_HISTORY_SYNC) {
          console.log('[useMapHistorySync] Skipping idle - handling history');
        }
        return;
      }

      // ═══════════════════════════════════════════════════════════
      // FIRST IDLE EVENT: Initialization Phase
      // ═══════════════════════════════════════════════════════════
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;

        if (DEBUG_MAP_HISTORY_SYNC) {
          console.log('[useMapHistorySync] FIRST IDLE - initialization phase');
        }

        // CASE A: fitBounds will be called next (AreaBoundary positioning)
        // AreaBoundary component calls skipNextIdleUpdate(map) before calling map.fitBounds()
        // We need to wait for the NEXT idle event (after fitBounds), not this one
        if (shouldSkipNextIdle(map)) {
          if (DEBUG_MAP_HISTORY_SYNC) {
            console.log('[useMapHistorySync] FIRST IDLE skipped - skipNextIdle is set');
          }
          return;
        }

        // Remember the settled initial view for clean-URL history restores, but
        // never rewrite URL params during initialization. If a shared URL says
        // z=16, the first idle must not replace it with a fitBounds-derived z=15.
        refineCurrentSearchMapHistoryView({ center: { lat, lng }, zoom });
        lastObservedViewRef.current = nextView;

        if (DEBUG_MAP_HISTORY_SYNC) {
          console.log('[useMapHistorySync] FIRST IDLE - initial view stored, URL untouched');
        }

        // Clean URLs stay clean, and parameterized URLs stay exactly as entered,
        // until the user actually interacts with the map.
        return;
      }

      // ═══════════════════════════════════════════════════════════
      // SECOND IDLE EVENT: After fitBounds completes
      // ═══════════════════════════════════════════════════════════
      // AreaBoundary called fitBounds(), map repositioned, and now it's idle again.
      // This is the optimal viewport/zoom we want to remember for restoration.
      // Clear the skip flag but don't update URL yet - keep URLs clean on initial load.
      if (shouldSkipNextIdle(map)) {
        if (DEBUG_MAP_HISTORY_SYNC) {
          console.log('[useMapHistorySync] SECOND IDLE - after fitBounds, storing initial view');
        }
        refineCurrentSearchMapHistoryView({ center: { lat, lng }, zoom });

        lastObservedViewRef.current = nextView;
        clearSkipNextIdle(map);
        return;
      }

      if (DEBUG_MAP_HISTORY_SYNC) {
        console.log('[useMapHistorySync] Post-initial idle - checking user intent before URL update');
      }

      // ═══════════════════════════════════════════════════════════
      // SUBSEQUENT IDLE EVENTS: User Interaction Phase
      // ═══════════════════════════════════════════════════════════

      // Programmatic pans (e.g., centering on pinned marker) set this flag
      // to prevent creating phantom browser history entries.
      if (suppressNextUrlUpdateMap.get(map)) {
        suppressNextUrlUpdateMap.delete(map);
        lastObservedViewRef.current = nextView;
        if (DEBUG_MAP_HISTORY_SYNC) {
          console.log('[useMapHistorySync] Suppressed URL update (programmatic pan)');
        }
        return;
      }

      if (!hasUserInteractedWithMapRef.current) {
        lastObservedViewRef.current = nextView;
        if (DEBUG_MAP_HISTORY_SYNC) {
          console.log('[useMapHistorySync] Skipping idle - no user map intent yet');
        }
        return;
      }

      if (!mapViewChanged(lastObservedViewRef.current, nextView)) {
        if (DEBUG_MAP_HISTORY_SYNC) {
          console.log('[useMapHistorySync] Skipping idle - map view unchanged');
        }
        return;
      }
      lastObservedViewRef.current = nextView;

      // User panned or zoomed the map - update URL with debouncing to avoid
      // spamming browser history with tiny movements during pan/zoom gestures.
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      const scheduledHref = `${window.location.pathname}${window.location.search}`;
      debounceTimerRef.current = setTimeout(() => {
        updateURL(lat, lng, zoom, scheduledHref);
      }, 300);
    };

    const listener = map.addListener('idle', handleIdle);

    return () => {
      google.maps.event.removeListener(listener);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [map, updateURL]);

  /**
   * Handle browser back/forward navigation
   *
   * When user clicks browser back/forward buttons, restore the map position
   * from URL parameters or the destination SRP history entry.
   */
  useEffect(() => {
    if (!map) {
      return;
    }

    const handlePopState = (event: PopStateEvent) => {
      const params = new URLSearchParams(window.location.search);
      const restoreView = readRestorableMapView(params, event.state);

      if (!restoreView) return;
      if (mapAlreadyMatchesPosition(map, restoreView.lat, restoreView.lng, restoreView.zoom)) {
        lastObservedViewRef.current = restoreView;
        return;
      }

      isHandlingHistoryRef.current = true;
      historyRestoreInFlightMap.set(map, true);

      const currentZoom = map.getZoom();
      map.panTo({ lat: restoreView.lat, lng: restoreView.lng });
      if (currentZoom !== restoreView.zoom) {
        map.setZoom(restoreView.zoom);
      }

      // Reset the history handling flag AFTER map finishes moving
      // We wait for the 'idle' event to ensure map has settled, then use setTimeout(0)
      // to defer flag reset until after ALL idle handlers have run.
      // This prevents a race condition where handleIdle tries to push to history
      // before we've finished handling the popstate.
      let listener: google.maps.MapsEventListener | null = null;
      let resetTimer: ReturnType<typeof setTimeout> | null = null;
      const resetHistoryHandling = () => {
        isHandlingHistoryRef.current = false;
        historyRestoreInFlightMap.delete(map);
        if (listener) {
          google.maps.event.removeListener(listener);
          listener = null;
        }
        if (resetTimer) {
          clearTimeout(resetTimer);
          resetTimer = null;
        }
      };

      listener = map.addListener('idle', () => {
        // Use setTimeout(0) to defer until after current event handlers complete
        setTimeout(() => {
          const settledCenter = map.getCenter();
          const settledZoom = map.getZoom();
          if (settledCenter && settledZoom !== undefined) {
            lastObservedViewRef.current = {
              lat: settledCenter.lat(),
              lng: settledCenter.lng(),
              zoom: settledZoom,
            };
          }
          resetHistoryHandling();
        }, 0);
      });
      resetTimer = setTimeout(() => {
        const settledCenter = map.getCenter();
        const settledZoom = map.getZoom();
        if (settledCenter && settledZoom !== undefined) {
          lastObservedViewRef.current = {
            lat: settledCenter.lat(),
            lng: settledCenter.lng(),
            zoom: settledZoom,
          };
        }
        resetHistoryHandling();
      }, 1000);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [map]);
}
